import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import { resolveSchema, type ResolveSchemaInput } from "src/api/schemas/resolveSchema";
import { JsonFormsState, ValidationError, SubmissionState } from "./types";
import type {
  AtozPreScreenData,
  CargoPreScreenData,
  IplPreScreenData,
  PreScreenState,
  WcPreScreenData,
} from "./preScreen/types";

export type { ResolveSchemaInput } from "src/api/schemas/resolveSchema";

/**
 * Async thunk that resolves a pre-screen schema via the `/resolveSchema`
 * service operation.
 *
 * Accepts a `ResolveSchemaInput` (lineOfBusiness, optional options) and
 * returns the compiled schema the UI needs for form rendering.
 *
 * Uses the same authenticated-client + Command pattern as `createClaim`.
 *
 * On rejection, `resolveSchemaInput` is intentionally left untouched so
 * the user can retry with the same input.
 */
export const fetchPreScreenSchema = createAsyncThunk<
  { input: ResolveSchemaInput; schema: CloudscapeJsonSchema; schemaId: string },
  ResolveSchemaInput,
  { rejectValue: string }
>("jsonForms/fetchPreScreenSchema", async (input, { rejectWithValue, signal }) => {
  try {
    const { schema, schemaId } = await resolveSchema(input, signal);
    return { input, schema, schemaId };
  } catch (err) {
    // Let abort errors propagate so RTK sets action.meta.aborted = true
    // on the rejected action. This allows the reducer to distinguish
    // between real failures and intentional cancellations (e.g. unmount).
    if (err instanceof Error && err.name === "AbortError") {
      throw err;
    }
    return rejectWithValue(
      err instanceof Error ? err.message : `Failed to resolve schema for ${input.lineOfBusiness}`
    );
  }
});

/**
 * Initial state for the pre-screening sub-state owned by the LOB-agnostic
 * `JsonFormsClaimIntakeApp`. All per-LOB slots default to `null`; the schema-fetch
 * lifecycle starts in `idle`.
 */
export const preScreenInitialState: PreScreenState = {
  wc: null,
  cargo: null,
  ipl: null,
  atoz: null,
  resolveSchemaInput: null,
  resolvedSchemaName: null,
  resolvedSchemaId: null,
  submissionCompleted: false,
  schemaFetchStatus: "idle",
  schemaFetchErrorMessage: null,
  fetchedSchema: null,
  schemaFetchRequestId: null,
  s3AttachmentsId: null,
};

/**
 * Initial state for the JSON Forms slice.
 * Form data and validation errors are keyed by `{lob}_{stage}`
 * (e.g., "cargo_stage1") to support multiple LOBs without collisions.
 *
 * This slice is completely separate from the existing `formsSlice`
 * and does not share any state keys with it.
 */
export const initialState: JsonFormsState = {
  formData: {},
  validationErrors: {},
  submission: {
    status: "idle",
    response: null,
    error: null,
  },
  preScreen: preScreenInitialState,
};

export const jsonFormsSlice = createSlice({
  name: "jsonForms",
  initialState,
  reducers: {
    setFormData: (state, action: PayloadAction<{ key: string; data: Record<string, unknown> }>) => {
      const { key, data } = action.payload;
      state.formData[key] = data;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<{ key: string; errors: ValidationError[] }>
    ) => {
      const { key, errors } = action.payload;
      state.validationErrors[key] = errors;
    },
    setSubmissionStatus: (state, action: PayloadAction<SubmissionState>) => {
      state.submission = action.payload;
    },
    resetFormData: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      delete state.formData[key];
    },
    resetAll: () => {
      return initialState;
    },
    setWcPreScreenData: (state, action: PayloadAction<WcPreScreenData | null>) => {
      state.preScreen.wc = action.payload;
    },
    setCargoPreScreenData: (state, action: PayloadAction<CargoPreScreenData | null>) => {
      state.preScreen.cargo = action.payload;
    },
    setIplPreScreenData: (state, action: PayloadAction<IplPreScreenData | null>) => {
      state.preScreen.ipl = action.payload;
    },
    setAtozPreScreenData: (state, action: PayloadAction<AtozPreScreenData | null>) => {
      state.preScreen.atoz = action.payload;
    },
    setResolvedSchemaName: (state, action: PayloadAction<string | null>) => {
      state.preScreen.resolvedSchemaName = action.payload;
    },
    setResolveSchemaInput: (state, action: PayloadAction<ResolveSchemaInput | null>) => {
      state.preScreen.resolveSchemaInput = action.payload;
    },
    resetPreScreen: (state) => {
      state.preScreen = preScreenInitialState;
    },
    /**
     * Marks that a claim submission completed successfully. This flag is
     * persisted so the rehydration logic on page reload knows to clear
     * the pre-screen instead of re-fetching the schema.
     */
    markSubmissionCompleted: (state) => {
      state.preScreen.submissionCompleted = true;
    },
    /**
     * Mints a fresh `s3AttachmentsId`, discarding any previous one.
     *
     * Dispatched when a pre-screen is accepted so each claim gets its own
     * attachments folder. Deliberately not dispatched on the rehydration
     * re-fetch after a page reload — rotating there would orphan files the
     * user already uploaded against the old id.
     */
    regenerateS3AttachmentsId: (state) => {
      state.preScreen.s3AttachmentsId = v4();
    },
    /**
     * Clears the schema-fetch lifecycle (status, error message, fetched
     * schema, resolve input) but leaves the captured per-LOB pre-screen
     * data intact. Used by the schema-fetch error path to send the user
     * back to the pre-screen without losing what they already typed.
     *
     * Also drops the `s3AttachmentsId`: this is the "start a new form" path,
     * and the next accepted pre-screen mints a new one.
     */
    clearSchemaFetchState: (state) => {
      state.preScreen.schemaFetchStatus = "idle";
      state.preScreen.schemaFetchErrorMessage = null;
      state.preScreen.fetchedSchema = null;
      state.preScreen.schemaFetchRequestId = null;
      state.preScreen.resolveSchemaInput = null;
      state.preScreen.resolvedSchemaName = null;
      state.preScreen.resolvedSchemaId = null;
      state.preScreen.s3AttachmentsId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreScreenSchema.pending, (state, action) => {
        state.preScreen.schemaFetchRequestId = action.meta.requestId;
        state.preScreen.schemaFetchStatus = "loading";
        state.preScreen.schemaFetchErrorMessage = null;
      })
      .addCase(fetchPreScreenSchema.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.preScreen.schemaFetchRequestId) {
          return;
        }
        state.preScreen.schemaFetchStatus = "success";
        state.preScreen.fetchedSchema = action.payload.schema;
        state.preScreen.resolvedSchemaId = action.payload.schemaId;
        state.preScreen.schemaFetchErrorMessage = null;
      })
      .addCase(fetchPreScreenSchema.rejected, (state, action) => {
        // Ignore a superseded request (e.g. aborted by a newer fetch or
        // dropped by Back to form): its late rejection must not reset the
        // newer request's `loading` state.
        if (action.meta.requestId !== state.preScreen.schemaFetchRequestId) {
          return;
        }
        // When the thunk is aborted (e.g. component unmount during
        // StrictMode double-mount, or navigating away mid-fetch), reset
        // to idle rather than surfacing a user-facing error. This allows
        // the rehydration effect on the next mount to retry the fetch.
        if (action.meta.aborted || action.error?.name === "AbortError") {
          state.preScreen.schemaFetchStatus = "idle";
          state.preScreen.schemaFetchErrorMessage = null;
          return;
        }
        state.preScreen.schemaFetchStatus = "error";
        state.preScreen.schemaFetchErrorMessage = action.payload ?? action.error.message ?? null;
      });
  },
});

export const {
  setFormData,
  setValidationErrors,
  setSubmissionStatus,
  resetFormData,
  resetAll,
  setWcPreScreenData,
  setCargoPreScreenData,
  setIplPreScreenData,
  setAtozPreScreenData,
  setResolvedSchemaName,
  setResolveSchemaInput,
  resetPreScreen,
  markSubmissionCompleted,
  regenerateS3AttachmentsId,
  clearSchemaFetchState,
} = jsonFormsSlice.actions;

export default jsonFormsSlice.reducer;
